import * as React from "react";
import Breadcrumbs from "../core/Breadcrumbs";
import PageContainer from "../core/PageContainer";
import {
  RailsUserContext,
  normalizeUserContextData,
} from "../../libs/user_context";
import EmployeeForm from "../employee/EmployeeForm";
import { normalizeUser, patchUser, disableUser } from "../../libs/user";
import { toast } from "../../utils";
import Card from "../core/Card";
import { wrapComponentForErrors } from "../utility/ErrorHandler";
import { useState } from "react";
import { normalizeOrg } from "../../libs/organization";
import { getAttributes } from "../../libs/api_utils";
import * as Sentry from "@sentry/browser";

interface IProps {
  employee: IJsonApiResponse<IUserRecord>;
  userContext: IUserContextData;
  org: IJsonApiResponse<IOrganizationRecord>;
}

function EmployeeEdit({
  userContext: rawUserContext,
  employee: { data: employeeRecord },
  org: { data: orgRecord },
}: IProps) {
  const [employee] = useState(normalizeUser(employeeRecord));
  const [org] = useState(normalizeOrg(orgRecord));

  const sharedCrumbs = [
    { label: employee.name, route: `/employees/${employee.id}` },
  ];
  const userContext = normalizeUserContextData(rawUserContext);
  const currentUser = userContext.user;
  const isAdmin = currentUser.isAdmin;

  const breadcrumbs = isAdmin
    ? [
        { label: "Organizations", route: "/organizations" },
        { label: org.name, route: `/organizations/${org.id}` },
        ...sharedCrumbs,
        { label: "Edit" },
      ]
    : [
        { label: "My Organization", route: "/organization" },
        ...sharedCrumbs,
        { label: "Edit" },
      ];

  return (
    <RailsUserContext.Provider value={userContext}>
      <PageContainer>
        <Breadcrumbs crumbs={breadcrumbs} />
        <Card title={"Edit User"}>
          <EmployeeForm
            employee={employee}
            org={org}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              const userAttrs = getAttributes(employee);
              Object.assign(userAttrs, values);
              patchUser(employee.id, userAttrs)
                .then(() => {
                  setSubmitting(false);
                  toast.success(`User saved!`);
                  Turbolinks.visit(`/employees/${employee.id}`);
                })
                .catch(e => {
                  setSubmitting(false);
                  toast.error("Error occurred while saving the user.");
                  Sentry.captureException(e);
                  if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.error(e);
                  }
                });
            }}
            disableUser={(userId, setSubmitting) => {
              setSubmitting(true);
              disableUser(userId)
                .then(resp => {
                  if (resp && resp.status === "ok") {
                    setSubmitting(false);
                    toast.success("User removed");
                    const nextRoute = currentUser.isAdmin
                      ? `/organizations/${employee.organizationId}`
                      : `/organization`;
                    Turbolinks.visit(nextRoute);
                  } else {
                    setSubmitting(false);
                    toast.error("Unable to remove user");
                  }
                })
                .catch(e => {
                  setSubmitting(false);
                  toast.error("Error removing user");
                  Sentry.captureException(e);
                  if (process.env.NODE_ENV !== "production") {
                    // eslint-disable-next-line no-console
                    console.error(e);
                  }
                });
            }}
          />
        </Card>
      </PageContainer>
    </RailsUserContext.Provider>
  );
}

export default wrapComponentForErrors(EmployeeEdit);



// WEBPACK FOOTER //
// ./app/javascript/components/pages/EmployeeEdit.tsx